import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { defaultPresaveServices } from 'fym-shared/src/smartlinks/presave';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { SMLUpgradeFeature } from '../../music24/config/music24PlanCapabilities';
import { useMusic24Capabilities } from '../../music24/hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../music24/hooks/useUpgradePrompt';
import { useIsNativeEmbed } from '../../music24/navigation/hooks/useIsNativeEmbed';
import { useOpenCoverEditor } from '../../coverEditor/entry/useOpenCoverEditor';
import { offersCoverEditor } from '../../coverEditor/entry/coverFieldState';
import { catalogArtworkOriginalSrc } from '../catalog/catalogArtwork';
import { usePresaveLockLabel } from '../presave/usePresaveLockLabel';
import { dayOfIso, PresaveSongState } from './presaveCreateFacts';
import { presaveCreateMessages } from './presaveCreateMessages';
import { formatReleaseDay } from './formatReleaseDay';
import { PresaveServiceChips } from './PresaveServiceChips';
import { PresaveSongCard } from './PresaveSongCard';
import { PresaveDateEditor } from './PresaveDateEditor';
import { CreatedPresave, useCreatePresaveFromSong } from './useCreatePresaveFromSong';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './PresaveCreate.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PresaveFromSongProps {
  accountId: number;
  item: CatalogItem;
  state: Extract<PresaveSongState, 'ready' | 'needsDate'>;
  today: string;
  onCreated: (page: CreatedPresave, services: string[]) => void;
  /** The page exists but is not published: the Creator takes over. */
  onDraft: (smartLinkId: number) => void;
  onOpenSongPage: (section?: 'details') => void;
  onClose: () => void;
}

/**
 * A pre-save made from a song (design presave-create a1-a5, d1): the song card, the service logos
 * (all on) and one button. Title, artist, cover and day come from the catalog row; nothing is typed.
 */
const PresaveFromSongComponent: React.FC<PresaveFromSongProps> = ({
  accountId,
  item,
  state,
  today,
  onCreated,
  onDraft,
  onOpenSongPage,
  onClose,
}) => {
  const intl = useIntl();
  const { canUsePresave } = useMusic24Capabilities();
  const openUpgrade = useUpgradePrompt();
  const lockLabel = usePresaveLockLabel();
  const isNativeEmbed = useIsNativeEmbed();
  const openCoverEditor = useOpenCoverEditor({ accountId, newTab: true });
  const [services, setServices] = useState<string[]>(defaultPresaveServices);
  const [isEditingDay, setIsEditingDay] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const { create, isCreating } = useCreatePresaveFromSong(accountId);

  const day = dayOfIso(item.releaseDate);
  const dayText = day === undefined ? undefined : formatReleaseDay(intl, day);
  const showDateEditor = state === 'needsDate' || isEditingDay;
  const canCreate = state === 'ready' && !isEditingDay && !isCreating;

  const onEditCover = useCallback(() => openCoverEditor(item.uuid), [item.uuid, openCoverEditor]);
  const onUploadCover = useCallback(() => onOpenSongPage('details'), [onOpenSongPage]);
  const onEditSong = useCallback(() => onOpenSongPage('details'), [onOpenSongPage]);
  const onChangeDay = useCallback(() => setIsEditingDay(true), []);
  const onDaySaved = useCallback(() => setIsEditingDay(false), []);
  const onCancelDay = useCallback(() => setIsEditingDay(false), []);
  const onServices = useCallback((next: string[]) => {
    setServices(next);
    setHasFailed(false);
  }, []);
  const onSeePlans = useCallback(() => openUpgrade(SMLUpgradeFeature.presave), [openUpgrade]);

  const onCreate = useCallback(() => {
    if (!canCreate) {
      return;
    }
    setHasFailed(false);
    create(item, services)
      .then((outcome) => {
        if (outcome.kind === 'created') {
          onCreated(outcome.page, services);
        } else if (outcome.kind === 'draft') {
          onDraft(outcome.smartLinkId);
        } else if (outcome.kind === 'failed') {
          setHasFailed(true);
        }
      })
      .catch(console.error);
  }, [canCreate, create, item, onCreated, onDraft, services]);

  let buttonMessage = presaveCreateMessages.create;
  if (isCreating) {
    buttonMessage = presaveCreateMessages.creating;
  } else if (hasFailed) {
    buttonMessage = presaveCreateMessages.tryAgain;
  }
  const coverOffered = offersCoverEditor({ isNativeEmbed });
  const hasCover = catalogArtworkOriginalSrc(item) !== undefined;
  let footNote: string | undefined;
  if (state === 'needsDate') {
    footNote = intl.formatMessage(presaveCreateMessages.needsDateFirst);
  } else if (!hasCover) {
    footNote = intl.formatMessage(presaveCreateMessages.noCoverNote);
  }

  return (
    <div className={styles.body}>
      <div className={styles.qa}>
        <div>
          <div className={styles.eyebrow}>{intl.formatMessage(presaveCreateMessages.eyebrow)}</div>
          <h1 className={styles.question}>
            {state === 'needsDate' || dayText === undefined
              ? intl.formatMessage(presaveCreateMessages.questionNoDate, { title: item.title })
              : intl.formatMessage(presaveCreateMessages.question, { title: item.title })}
          </h1>
          <p className={styles.sub}>
            {state === 'needsDate' || day === undefined
              ? intl.formatMessage(presaveCreateMessages.subNoDate)
              : intl.formatMessage(presaveCreateMessages.sub, { date: formatReleaseDay(intl, day, false) })}
          </p>
        </div>
        {hasFailed && (
          <div className={styles.alertError} role="alert" data-testid="presave-create-error">
            <FontAwesomeIcon icon={faCircleExclamation} className={styles.alertIcon} />
            <span>{intl.formatMessage(presaveCreateMessages.createFailed)}</span>
          </div>
        )}
        <PresaveServiceChips selected={services} onChange={onServices} disabled={isCreating || !canUsePresave} />
        {!canUsePresave && (
          <div className={styles.lockLine} data-testid="presave-create-lock">
            <FontAwesomeIcon icon={faLock} className={styles.lockIcon} />
            <span>
              {intl.formatMessage(presaveCreateMessages.lockLine)}
              {lockLabel !== null && (
                <>
                  <br />
                  <span className={styles.lockChip}>{lockLabel}</span>
                </>
              )}
            </span>
          </div>
        )}
        <div className={styles.actions}>
          <span className={styles.actionsNote}>
            {canUsePresave && intl.formatMessage(presaveCreateMessages.publishNote)}
          </span>
          {canUsePresave ? (
            <SMLButton
              color="primary"
              size="lg"
              omitCap
              disabled={!canCreate}
              isLoading={isCreating}
              onClick={onCreate}
              testID="presave-create-submit"
            >
              {intl.formatMessage(buttonMessage)}
            </SMLButton>
          ) : (
            <>
              <SMLButton outline omitCap onClick={onClose}>
                {intl.formatMessage(presaveCreateMessages.notNow)}
              </SMLButton>
              <SMLButton color="primary" size="lg" omitCap onClick={onSeePlans} testID="presave-create-plans">
                {intl.formatMessage(presaveCreateMessages.seePlans)}
              </SMLButton>
            </>
          )}
        </div>
        {footNote !== undefined && <span className={styles.hint}>{footNote}</span>}
      </div>
      <PresaveSongCard
        item={item}
        pill="upcoming"
        dayText={dayText}
        onChangeDay={state === 'ready' && !isEditingDay ? onChangeDay : undefined}
        dateEditor={
          showDateEditor ? (
            <PresaveDateEditor
              accountId={accountId}
              itemUuid={item.uuid}
              initialDay={day ?? ''}
              today={today}
              onCancel={state === 'ready' ? onCancelDay : undefined}
              onSaved={onDaySaved}
            />
          ) : undefined
        }
        onEditCover={coverOffered ? onEditCover : undefined}
        onUploadCover={onUploadCover}
        onEditSong={onEditSong}
      />
    </div>
  );
};

export const PresaveFromSong = TypedMemo(PresaveFromSongComponent);
